import { useState, useEffect, useContext, useCallback } from 'react';
import { ApiContext } from '../../App';
import { FaFilePdf, FaFileExcel, FaFileWord, FaFileImage, FaFile, FaDownload, FaTrash, FaUpload, FaEye, FaLink, FaPlus } from 'react-icons/fa';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const PositionDocuments = ({ positionId }) => {
  const API = useContext(ApiContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // États pour l'ajout d'URL
  const [showUrlForm, setShowUrlForm] = useState(false);
  const [urlData, setUrlData] = useState({ url: '', originalName: '', description: '' });
  const [addingUrl, setAddingUrl] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get(`/api/positions/${positionId}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des documents:', error);
    } finally {
      setLoading(false);
    }
  }, [API, positionId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    const formData = new FormData();
    formData.append('document', selectedFile);

    try {
      setUploading(true);
      await API.post(`/api/positions/${positionId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSelectedFile(null);
      // Reset file input
      document.getElementById('file-input').value = '';
      fetchDocuments();
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      alert('Erreur lors de l\'upload du document');
    } finally {
      setUploading(false);
    }
  };

  const handleView = (documentId) => {
    // Construire l'URL de visualisation avec le token d'authentification
    const token = localStorage.getItem('token'); // Ou la méthode que vous utilisez pour stocker le token
    const baseURL = API.defaults.baseURL || '';
    const viewUrl = `${baseURL}/api/positions/${positionId}/documents/${documentId}/view`;

    // Ouvrir dans un nouvel onglet
    window.open(viewUrl, '_blank');
  };

  const handleDownload = (documentId) => {
    // Construire l'URL de téléchargement (le backend redirige vers S3 avec Content-Disposition: attachment)
    const baseURL = API.defaults.baseURL || '';
    const downloadUrl = `${baseURL}/api/positions/${positionId}/documents/${documentId}/download`;

    // Ouvrir dans un nouvel onglet - le navigateur téléchargera automatiquement grâce à l'en-tête Content-Disposition
    window.open(downloadUrl, '_blank');
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    const documentId = itemToDelete;
    setShowDeleteDialog(false);
    setItemToDelete(null);

    try {
      await API.delete(`/api/positions/${positionId}/documents/${documentId}`);
      fetchDocuments();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du document');
    }
  };

  const handleAddUrl = async () => {
    if (!urlData.url || !urlData.originalName) {
      alert('Veuillez renseigner l\'URL et le nom');
      return;
    }

    try {
      setAddingUrl(true);
      await API.post(`/api/positions/${positionId}/documents/url`, urlData);
      setUrlData({ url: '', originalName: '', description: '' });
      setShowUrlForm(false);
      fetchDocuments();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'URL:', error);
      alert('Erreur lors de l\'ajout de l\'URL');
    } finally {
      setAddingUrl(false);
    }
  };

  const getFileIcon = (mimetype, isUrl) => {
    if (isUrl || mimetype === 'application/link') return <FaLink className="text-blue-500" />;
    if (mimetype.includes('pdf')) return <FaFilePdf className="text-red-500" />;
    if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) return <FaFileExcel className="text-green-500" />;
    if (mimetype.includes('word') || mimetype.includes('document')) return <FaFileWord className="text-blue-500" />;
    if (mimetype.includes('image')) return <FaFileImage className="text-purple-500" />;
    return <FaFile className="text-gray-500" />;
  };

  const canBeViewed = (mimetype) => {
    // Les PDFs et images peuvent être visualisés dans le navigateur
    return mimetype.includes('pdf') || mimetype.includes('image');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg border p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Documents</h2>

      {/* Upload Section */}
      <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <input
            id="file-input"
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.xlsx,.xls,.doc,.docx,.jpg,.jpeg,.png"
            className="w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-2 file:px-3 sm:file:px-4 file:rounded-lg file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
          >
            <FaUpload />
            {uploading ? 'Upload...' : 'Uploader'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Formats acceptés : PDF, Excel, Word, Images (max 10MB)
        </p>
      </div>

      {/* Add URL Section */}
      <div className="mb-6 sm:mb-8">
        {!showUrlForm ? (
          <button
            onClick={() => setShowUrlForm(true)}
            className="w-full sm:w-auto px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-300 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
          >
            <FaLink className="text-gray-500" />
            Ajouter un lien
          </button>
        ) : (
          <div className="p-4 bg-white rounded-lg border border-gray-300">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Nom du lien <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={urlData.originalName}
                  onChange={(e) => setUrlData({ ...urlData, originalName: e.target.value })}
                  placeholder="Ex: Dossier Google Drive"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={urlData.url}
                  onChange={(e) => setUrlData({ ...urlData, url: e.target.value })}
                  placeholder="https://drive.google.com/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleAddUrl}
                  disabled={addingUrl}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                >
                  <FaPlus />
                  {addingUrl ? 'Ajout...' : 'Ajouter'}
                </button>
                <button
                  onClick={() => {
                    setShowUrlForm(false);
                    setUrlData({ url: '', originalName: '', description: '' });
                  }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="text-center py-6 sm:py-8">
          <p className="text-sm sm:text-base text-gray-500">Chargement...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-6 sm:py-8">
          <p className="text-sm sm:text-base text-gray-500">Aucun document pour cette position</p>
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {documents.map((doc) => (
            <div
              key={doc._id}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition gap-3 sm:gap-0"
            >
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                <div className="text-xl sm:text-2xl flex-shrink-0">
                  {getFileIcon(doc.mimetype, doc.isUrl)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate m-0">
                    {doc.originalName}
                  </h3>
                  {doc.isUrl ? (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-0.5 block truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {doc.url}
                    </a>
                  ) : (
                    <p className="text-xs sm:text-sm text-gray-500">
                      {formatFileSize(doc.size)}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(doc.uploadedAt)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 justify-end sm:justify-start">
                {!doc.isUrl && (
                  <>
                    {canBeViewed(doc.mimetype) && (
                      <button
                        onClick={() => handleView(doc._id)}
                        className="p-2 sm:p-2.5 text-green-600 hover:bg-green-50 rounded-lg transition flex-1 sm:flex-none"
                        title="Visualiser"
                      >
                        <FaEye className="text-sm sm:text-base" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(doc._id)}
                      className="p-2 sm:p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition flex-1 sm:flex-none"
                      title="Télécharger"
                    >
                      <FaDownload className="text-sm sm:text-base" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setItemToDelete(doc._id);
                    setShowDeleteDialog(true);
                  }}
                  className="p-2 sm:p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition flex-1 sm:flex-none"
                  title="Supprimer"
                >
                  <FaTrash className="text-sm sm:text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Supprimer le document"
        message="Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default PositionDocuments;
